import { useState } from 'react';
import { 
	Star, 
	ThumbsUp, 
	ThumbsDown, 
	MessageSquare, 
	Shield, 
	Clock,
	MoreHorizontal,
	Edit,
	Trash2,
	Flag,
	Image as ImageIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useUserStore } from '../stores/useUserStore';

const ReviewCard = ({ 
	review, 
	onVote, 
	onDelete, 
	onEdit, 
	onReport,
	showActions = true,
	isOwner = false,
	isAdmin = false 
}) => {
	const [showFullContent, setShowFullContent] = useState(false);
	const [showImages, setShowImages] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [showActionsMenu, setShowActionsMenu] = useState(false);
	const { user } = useUserStore();

	// חישוב כוכבים
	const renderStars = (rating, size = "w-4 h-4") => {
		return Array.from({ length: 5 }, (_, index) => (
			<Star
				key={index}
				className={`${size} ${
					index < rating
						? "text-yellow-400 fill-current"
						: "text-gray-300"
				}`}
			/>
		));
	};

	// רנדור דירוגים מפורטים
	const renderDetailedRatings = () => {
		const ratings = [
			{ key: 'quality', label: 'איכות', value: review.ratings?.quality },
			{ key: 'design', label: 'עיצוב', value: review.ratings?.design },
			{ key: 'value', label: 'תמורה', value: review.ratings?.value },
			{ key: 'comfort', label: 'נוחות', value: review.ratings?.comfort }
		];

		const hasDetailedRatings = ratings.some(r => r.value !== null && r.value !== undefined);
		if (!hasDetailedRatings) return null;

		return (
			<div className="mt-5 p-3 bg-gray-700/30 rounded-lg">
				<h4 className="text-sm font-medium text-gray-300 mb-2">דירוגים מפורטים</h4>
				<div className="grid grid-cols-2 gap-2">
					{ratings.map(rating => rating.value && (
						<div key={rating.key} className="flex items-center justify-between">
							<span className="text-xs text-gray-400">{rating.label}</span>
							<div className="flex items-center gap-1">
								{renderStars(rating.value, "w-3 h-3")}
								<span className="text-xs text-gray-300 ml-1">{rating.value}</span>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	// רנדור יתרונות וחסרונות
	const renderProsAndCons = () => {
		if ((!review.pros || review.pros.length === 0) && 
		    (!review.cons || review.cons.length === 0)) {
			return null;
		}

		return (
			<div className="mt-4 space-y-3">
				{review.pros && review.pros.length > 0 && (
					<div>
						<h4 className="text-sm font-medium text-green-400 mb-2">יתרונות</h4>
						<ul className="space-y-1">
							{review.pros.map((pro, index) => (
								<li key={index} className="text-sm text-gray-300 flex items-start gap-2">
									<span className="text-green-400 text-xs mt-1">✓</span>
									{pro}
								</li>
							))}
						</ul>
					</div>
				)}
				
				{review.cons && review.cons.length > 0 && (
					<div>
						<h4 className="text-sm font-medium text-red-400 mb-2">חסרונות</h4>
						<ul className="space-y-1">
							{review.cons.map((con, index) => (
								<li key={index} className="text-sm text-gray-300 flex items-start gap-2">
									<span className="text-red-400 text-xs mt-1">✗</span>
									{con}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	};

	// רנדור תמונות
	const renderImages = () => {
		if (!review.images || review.images.length === 0) return null;

		return (
			<div className="mt-4">
				<div className="flex items-center gap-2 mb-2">
					<ImageIcon className="w-4 h-4 text-gray-400" />
					<span className="text-sm text-gray-400">תמונות הביקורת</span>
				</div>
				
				<div className="grid grid-cols-3 gap-2">
					{review.images.slice(0, 3).map((image, index) => (
						<div
							key={index}
							className="relative cursor-pointer rounded-lg overflow-hidden bg-gray-700 aspect-square"
							onClick={() => {
								setSelectedImageIndex(index);
								setShowImages(true);
							}}
						>
							<img
								src={image.url}
								alt={image.alt || `תמונת ביקורת ${index + 1}`}
								className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
							/>
							{index === 2 && review.images.length > 3 && (
								<div className="absolute inset-0 bg-black/60 flex items-center justify-center">
									<span className="text-white font-medium">
										+{review.images.length - 3}
									</span>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		);
	};

	// טיפול בהצבעה
	const handleVote = (voteType) => {
		if (!user) return;
		onVote?.(review._id, voteType);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-emerald-500/30 transition-all duration-300"
			dir="rtl"
		>
			{/* כותרת הביקורת */}
			<div className="flex items-start justify-between mb-4">
				<div className="flex-1">
					<div className="flex items-center gap-3 mb-2">
						{/* אווטר משתמש */}
						<div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
							{review.user?.avatar ? (
								<img 
									src={review.user.avatar} 
									alt={review.user.name}
									className="w-full h-full rounded-full object-cover"
								/>
							) : (
								<span className="text-white font-bold text-sm">
									{review.user?.name?.charAt(0) || 'א'}
								</span>
							)}
						</div>

						<div className="flex-1">
							<div className="flex items-center gap-2">
								<h4 className="font-semibold text-white">{review.user?.name || 'משתמש אנונימי'}</h4>
								
								{/* תגיות סטטוס */}
								{review.verifiedPurchase && (
									<div className="flex items-center gap-1 bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full text-xs">
										<Shield className="w-3 h-3" />
										<span>רכישה מאומתת</span>
									</div>
								)}
								
								{review.isRecommended && (
									<div className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded-full text-xs font-medium">
										מומלץ
									</div>
								)}
							</div>

							<div className="flex items-center gap-2 mt-1">
								<div className="flex items-center gap-1">
									{renderStars(review.ratings?.overall || 0)}
									<span className="text-sm text-gray-300 ml-1">
										{review.ratings?.overall || 0}.0
									</span>
								</div>
								
								<div className="flex items-center gap-1 text-xs text-gray-400">
									<Clock className="w-3 h-3" />
									<span>{review.timeAgo}</span>
								</div>
							</div>
						</div>
					</div>

					{/* כותרת הביקורת */}
					<h3 className="text-lg font-bold text-white mb-2">
						{review.title}
					</h3>
				</div>

				{/* תפריט פעולות */}
				{showActions && (isOwner || isAdmin) && (
					<div className="relative">
						<button
							onClick={() => setShowActionsMenu(!showActionsMenu)}
							className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50"
						>
							<MoreHorizontal className="w-4 h-4" />
						</button>

						{showActionsMenu && (
							<div className="absolute left-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 min-w-[120px]">
								{isOwner && (
									<>
										<button
											onClick={() => {
												onEdit?.(review);
												setShowActionsMenu(false);
											}}
											className="w-full px-3 py-2 text-right text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
										>
											<Edit className="w-3 h-3" />
											ערוך
										</button>
										<button
											onClick={() => {
												onDelete?.(review._id);
												setShowActionsMenu(false);
											}}
											className="w-full px-3 py-2 text-right text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
										>
											<Trash2 className="w-3 h-3" />
											מחק
										</button>
									</>
								)}
								
								{!isOwner && (
									<button
										onClick={() => {
											onReport?.(review._id);
											setShowActionsMenu(false);
										}}
										className="w-full px-3 py-2 text-right text-sm text-yellow-400 hover:bg-gray-700 flex items-center gap-2"
									>
										<Flag className="w-3 h-3" />
										דווח
									</button>
								)}
							</div>
						)}
					</div>
				)}
			</div>

			{/* תוכן הביקורת */}
			<div className="mb-4">
				<p className="text-gray-300 leading-relaxed">
					{showFullContent || review.content.length <= 200
						? review.content
						: `${review.content.substring(0, 200)}...`
					}
				</p>
				
				{review.content.length > 200 && (
					<button
						onClick={() => setShowFullContent(!showFullContent)}
						className="text-emerald-400 hover:text-emerald-300 text-sm mt-2 transition-colors"
					>
						{showFullContent ? "הצג פחות" : "הצג עוד"}
					</button>
				)}
			</div>

			{/* דירוגים מפורטים */}
			{renderDetailedRatings()}

			{/* יתרונות וחסרונות */}
			{renderProsAndCons()}

			{/* תמונות */}
			{renderImages()}

			{/* תגובת אדמין */}
			{review.adminResponse && (
				<div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
					<div className="flex items-center gap-2 mb-2">
						<div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
							<span className="text-white text-xs font-bold">SHA</span>
						</div>
						<span className="text-emerald-300 font-medium text-sm">תגובת המוכר</span>
						<span className="text-xs text-gray-400">
							{new Date(review.adminResponse.respondedAt).toLocaleDateString('he-IL')}
						</span>
					</div>
					<p className="text-gray-300 text-sm leading-relaxed">
						{review.adminResponse.content}
					</p>
				</div>
			)}

			{/* פעולות - הצבעה ותגובות */}
			<div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
				<div className="flex items-center gap-4">
					{/* הצבעה */}
					<div className="flex items-center gap-2">
						<button
							onClick={() => handleVote('helpful')}
							disabled={!user}
							className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gray-700/50 hover:bg-emerald-500/20 text-gray-300 hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<ThumbsUp className="w-4 h-4" />
							<span className="text-sm">{review.helpfulVotes || 0}</span>
						</button>
						
						<button
							onClick={() => handleVote('not-helpful')}
							disabled={!user}
							className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gray-700/50 hover:bg-red-500/20 text-gray-300 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<ThumbsDown className="w-4 h-4" />
							<span className="text-sm">{review.notHelpfulVotes || 0}</span>
						</button>
					</div>

					{/* שימושיות */}
					{(review.helpfulVotes > 0 || review.notHelpfulVotes > 0) && (
						<div className="text-xs text-gray-400">
							{review.helpfulnessPercentage}% מהמשתמשים מצאו את זה שימושי
						</div>
					)}
				</div>

				{/* כפתור תגובה */}
				<button className="flex items-center gap-1 text-gray-400 hover:text-emerald-300 transition-colors">
					<MessageSquare className="w-4 h-4" />
					<span className="text-sm">הגב</span>
				</button>
			</div>

			{/* מודל תמונות */}
			{showImages && (
				<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md">
					<div className="relative w-full h-full flex items-center justify-center p-4">
						<button
							onClick={() => setShowImages(false)}
							className="absolute top-4 left-4 text-white text-3xl bg-black/50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-colors z-10"
						>
							×
						</button>
						
						<img
							src={review.images[selectedImageIndex]?.url}
							alt={review.images[selectedImageIndex]?.alt}
							className="max-w-full max-h-full object-contain rounded-lg"
						/>
						
						{review.images.length > 1 && (
							<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
								{review.images.map((_, index) => (
									<button
										key={index}
										onClick={() => setSelectedImageIndex(index)}
										className={`w-3 h-3 rounded-full transition-colors ${
											index === selectedImageIndex 
												? 'bg-emerald-400' 
												: 'bg-white/30 hover:bg-white/50'
										}`}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</motion.div>
	);
};

export default ReviewCard; 